import { ArraySchema } from "@colyseus/schema"
import { useTranslation } from "react-i18next"
import { SynergyTriggers } from "../../../../../config"
import {
  IGameRecord,
  IPokemonRecord
} from "../../../../../models/colyseus-models/game-record"
import { computeSynergies } from "../../../../../models/colyseus-models/synergies"
import PokemonFactory from "../../../../../models/pokemon-factory"
import { getRankLabel } from "../../../../../types/strings/Strings"
import { Synergy } from "../../../../../types/enum/Synergy"
import { formatDate } from "../../utils/date"
import Team from "../after/team"
import { GameModeIcon } from "../icons/game-mode-icon"
import SynergyIcon from "../icons/synergy-icon"
import { EloBadge } from "./elo-badge"
import { GamePokemonDetailTooltip } from "../game/game-pokemon-detail"
import { ItemDetailTooltip } from "../../../game/components/item-detail"
import "./game-history-detail.css"

export default function GameHistoryDetail(props: {
  game: IGameRecord
  onClose: () => void
}) {
  const { t } = useTranslation()
  const { game, onClose } = props

  const synergies = getAllTriggeredSynergies(game.pokemons)

  return (
    <div className="game-history-detail-overlay" onClick={onClose}>
      <div
        className="game-history-detail my-container"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="bubbly blue close-btn" onClick={onClose}>
          {t("back_to_history")}
        </button>

        <div className="detail-header">
          <div className="detail-rank">
            {game.rank <= 3 && (
              <img src={`/assets/ui/rank${game.rank}.png`} alt="" />
            )}
            <span>{getRankLabel(game.rank)}</span>
          </div>

          <p className="detail-gamemode">
            <GameModeIcon gameMode={game.gameMode} />
            {t(`game_modes.${game.gameMode}`)}
          </p>

          <EloBadge elo={game.elo} />

          <p className="detail-date">{formatDate(game.time)}</p>
        </div>

        <div className="detail-team">
          <h3>{t("team")}</h3>
          <Team team={game.pokemons} />
        </div>

        <div className="detail-synergies">
          <h3>{t("synergies")}</h3>
          <ul className="synergies-list">
            {synergies.map(([type, value]) => (
              <li key={type}>
                <SynergyIcon type={type} />
                <span>{value}</span>
              </li>
            ))}
          </ul>
        </div>

        <GamePokemonDetailTooltip origin="after" />
        <ItemDetailTooltip />
      </div>
    </div>
  )
}

function getAllTriggeredSynergies(
  team: IPokemonRecord[] | ArraySchema<IPokemonRecord>
): [Synergy, number][] {
  const synergies = computeSynergies(
    team.map((pkmRecord) => {
      const pkm = PokemonFactory.createPokemonFromName(pkmRecord.name)
      pkm.positionY = 1
      pkmRecord.items.forEach((item) => {
        pkm.items.add(item)
      })
      return pkm
    })
  )

  return [...synergies.entries()]
    .filter(([type, value]) => value >= SynergyTriggers[type][0])
    .sort((a, b) => {
      const [typeA, valueA] = a
      const [typeB, valueB] = b
      const aTriggerReached = SynergyTriggers[typeA].filter(
        (n) => valueA >= n
      ).length
      const bTriggerReached = SynergyTriggers[typeB].filter(
        (n) => valueB >= n
      ).length
      return aTriggerReached !== bTriggerReached
        ? bTriggerReached - aTriggerReached
        : valueB - valueA
    })
}
